/**
 * Firebase Remote Config client boundary.
 *
 * This module is safe to import from shared code: the Remote Config SDK and the
 * Firebase app are loaded only after the browser check below.
 */
export type RemoteConfigDefaults = Readonly<Record<string, string>>;

export type RemoteConfigValues<TDefaults extends RemoteConfigDefaults> = {
  [Key in keyof TDefaults]: string;
};

const DEVELOPMENT_FETCH_INTERVAL_MS = 60_000;
const PRODUCTION_FETCH_INTERVAL_MS = 12 * 60 * 60 * 1_000;

const registeredDefaults: Record<string, string> = {};

type FirebaseRemoteConfig = Awaited<
  ReturnType<(typeof import('firebase/remote-config'))['getRemoteConfig']>
>;

type FirebaseRemoteConfigModule = typeof import('firebase/remote-config');

let remoteConfigPromise: Promise<{
  client: FirebaseRemoteConfig;
  module: FirebaseRemoteConfigModule;
} | null> | null = null;

const getRemoteConfigClient = async () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!remoteConfigPromise) {
    remoteConfigPromise = (async () => {
      const remoteConfigModule = await import('firebase/remote-config');

      if (!(await remoteConfigModule.isSupported())) {
        return null;
      }

      const { app } = await import('@/lib/firebaseConfig');
      const client = remoteConfigModule.getRemoteConfig(app);

      client.settings.minimumFetchIntervalMillis =
        process.env.NODE_ENV === 'development'
          ? DEVELOPMENT_FETCH_INTERVAL_MS
          : PRODUCTION_FETCH_INTERVAL_MS;

      return { client, module: remoteConfigModule };
    })().catch(() => null);
  }

  return remoteConfigPromise;
};

/**
 * Reads named client parameters without exposing Firebase SDK details to callers.
 *
 * Values come from the activated Remote Config cache after a successful fetch.
 * On unsupported browsers, fetch failure, throttling, or missing values, Firebase
 * falls back to the supplied in-app defaults.
 */
export const getRemoteConfigValues = async <
  TDefaults extends RemoteConfigDefaults,
>(
  defaults: TDefaults
): Promise<RemoteConfigValues<TDefaults>> => {
  const remoteConfig = await getRemoteConfigClient();

  if (!remoteConfig) {
    return { ...defaults } as RemoteConfigValues<TDefaults>;
  }

  Object.assign(registeredDefaults, defaults);
  remoteConfig.client.defaultConfig = registeredDefaults;

  try {
    await remoteConfig.module.fetchAndActivate(remoteConfig.client);
  } catch {
    // getValue() still returns the activated cache, or the in-app default.
  }

  return Object.keys(defaults).reduce(
    (values, key) => {
      values[key] = remoteConfig.module
        .getValue(remoteConfig.client, key)
        .asString();
      return values;
    },
    {} as Record<string, string>
  ) as RemoteConfigValues<TDefaults>;
};
